import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { FaceRecognitionStatusEnum } from '@goldcast/api/content';
import { getCaptionsForSegment } from '@goldcast/player-config/src/utils/playerConfigUtils';
import {
  getAllCompositeSegments,
  getClipFPS,
  isClipFromUploads,
  isClipPreRecordedEvent,
  isFullLengthClip
} from '@goldcast/player-config/src/utils/clipUtils';
import type { Clip } from '@goldcast/player-config/src/types/clip-types';
import type { SpeakersAnalysis } from '@goldcast/player-config/src/types/speakers-analysis';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { approxEquals } from '@goldcast/player-config/src/utils/compareUtils';
import { CLIP_UPDATE_TYPE } from '../clipUpdateDataUtilsWithSplits/constants';
import { updateClipDataWithSplits } from '../clipUpdateDataUtilsWithSplits/updateWithSplits';
import { remotionPlayerStateStore } from '../store/remotionPlayerState';
import { getSegmentAtCurrentFrame, getSegmentTimeFromPlayerTime, getPlayerTimeFromSegmentTime } from '../timeUtils';
import { snapCuePointToWordBoundary } from '../snapToWordBoundaryUtil';
import { snapCuePointToSentenceBoundary } from '../snapToSentenceBoundaryUtil';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import featureFlagStore from '@/stores/featureFlagStore';
import { showErrorToast } from '@/libs/toast/toast';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { isSplitsFeatureEnabled, isFullVideoFaceRecEnabledForClip } from '@/stores/clip';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { core } from '@/stores/core';

const FACE_REC_NON_APPLICABLE_STATUSES: FaceRecognitionStatusEnum[] = ['NOT_APPLICABLE', 'UNAVAILABLE', 'FAILED'];

/**
 * Returns true if the splits warning dialog should be shown before allowing
 * the user to create a split. The dialog warns that enabling splits will
 * disable speaker-based layouts.
 *
 * Show the dialog only for uploads and pre-recorded clips when ALL of these hold:
 *  - the clip is NOT full-length,
 *  - speakersAnalysis.accurate is true (vision data was computed and is reliable), AND
 *  - either: the full-video face-rec FF is off, or the face_recognition_status is
 *    NOT_APPLICABLE, UNAVAILABLE, or FAILED.
 */
export function shouldShowSplitsWarningDialog(clip: Clip, speakersAnalysis: SpeakersAnalysis): boolean {
  if (!isClipFromUploads(clip) && !isClipPreRecordedEvent(clip)) {
    return false;
  }
  if (isFullLengthClip(clip) && (clip.asset_metadata.duration ?? 0) > 180) {
    return false;
  }
  if (!speakersAnalysis.accurate) {
    return false;
  }
  const faceRecStatus = core.getSnapshot().content?.face_recognition_status;
  return (
    !isFullVideoFaceRecEnabledForClip(clip) ||
    (faceRecStatus !== undefined && FACE_REC_NON_APPLICABLE_STATUSES.includes(faceRecStatus))
  );
}

const MIN_SPLIT_DISTANCE_SECONDS = 3;

export type SplitMode = 'precise' | 'word' | 'sentence';

const REASON_FOR_DISABLED_SPLITS = {
  NOT_ENABLED: 'Splits are not enabled for this clip',
  TRANSITION_RECORDING: 'Splits are not supported for transition recordings',
  NOT_COMPOSITE_SEGMENT: 'Splits are not supported for this segment',
  NOT_ENOUGH_DISTANCE: 'Not enough distance to the nearest end of the composite segment'
};

export default function useSplits() {
  const { clipData } = useClipsContext();
  const { seekTo } = useRemotionPlayerSeek();

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);

  const { currentTime } = useSyncExternalStore(
    remotionPlayerStateStore.subscribe,
    remotionPlayerStateStore.getSnapshot
  );

  const fps = getClipFPS(clipData);

  const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
  const isCurrentSegmentComposite = currentSegment?.type === SegmentType.COMPOSITE;

  const distanceToNearestEndOfCompositeSegment = isCurrentSegmentComposite
    ? Math.min(
        Math.abs(currentSegment.toFrame - currentTime * fps),
        Math.abs(currentSegment.fromFrame - currentTime * fps)
      )
    : Infinity;

  const isTranslated = useMemo(() => {
    const originalLang = clipData.asset_metadata.original_lang;
    const selectedLang = clipData.asset_metadata.selected_lang;
    return originalLang && selectedLang && originalLang !== selectedLang;
  }, [clipData]);

  const isSplitsFeatureFlagEnabled = useMemo(() => {
    return isSplitsFeatureEnabled(featureFlags, clipData);
  }, [clipData, featureFlags]);

  const canUseSplits = useMemo(() => {
    return (
      isCurrentSegmentComposite &&
      distanceToNearestEndOfCompositeSegment > MIN_SPLIT_DISTANCE_SECONDS * fps &&
      isSplitsFeatureFlagEnabled &&
      !isTranslated
    );
  }, [
    isCurrentSegmentComposite,
    distanceToNearestEndOfCompositeSegment,
    fps,
    isSplitsFeatureFlagEnabled,
    isTranslated
  ]);

  const reasonForDisabledSplits = useMemo(() => {
    if (isTranslated) {
      return REASON_FOR_DISABLED_SPLITS.TRANSITION_RECORDING;
    }
    if (!isSplitsFeatureFlagEnabled) {
      return REASON_FOR_DISABLED_SPLITS.NOT_ENABLED;
    }
    if (!isCurrentSegmentComposite) {
      return REASON_FOR_DISABLED_SPLITS.NOT_COMPOSITE_SEGMENT;
    }
    if (distanceToNearestEndOfCompositeSegment <= MIN_SPLIT_DISTANCE_SECONDS * fps) {
      return REASON_FOR_DISABLED_SPLITS.NOT_ENOUGH_DISTANCE;
    }
    return '';
  }, [
    distanceToNearestEndOfCompositeSegment,
    fps,
    isCurrentSegmentComposite,
    isSplitsFeatureFlagEnabled,
    isTranslated
  ]);

  const hasExistingSplitAtPlayerTime = useCallback(
    (playerTime: number) => {
      const compositeSegments = getAllCompositeSegments(clipData);
      const toleranceSeconds = 0.5 / fps;
      return compositeSegments.some(segment => approxEquals(playerTime, segment.fromFrame / fps, toleranceSeconds));
    },
    [clipData, fps]
  );

  const performSplit = useCallback(
    (mode: SplitMode, resetLayoutToDefault = false) => {
      const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
      const segment = getSegmentAtCurrentFrame(currentTime, clipData);
      let startTime = currentTime;
      if (segment?.type === SegmentType.COMPOSITE) {
        const segmentTime = getSegmentTimeFromPlayerTime(currentTime, clipData, segment.id);
        const captions =
          getCaptionsForSegment({
            clip: clipData,
            segmentId: segment.id,
            isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
          }) ?? [];
        const snappedSegmentTime =
          mode === 'precise'
            ? segmentTime
            : mode === 'word'
              ? snapCuePointToWordBoundary(segmentTime, captions)
              : snapCuePointToSentenceBoundary(segmentTime, captions);
        startTime = getPlayerTimeFromSegmentTime(snappedSegmentTime, clipData, segment.id);
      }

      if (mode !== 'precise' && hasExistingSplitAtPlayerTime(startTime)) {
        showErrorToast('Split already exists at this location');
        return;
      }

      if (resetLayoutToDefault) {
        // Apply the split without committing to the store/backend, then layer the
        // layout reset on top so both mutations land in a single write.
        const splitClip = updateClipDataWithSplits(
          { clipId: clipData.id, actionType: CLIP_UPDATE_TYPE.SPLIT_SEGMENT, startTime },
          { skipStoreAndBackendUpdate: true }
        );
        updateClipDataWithSplits(
          { clipId: clipData.id, actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT, layout: 'DEFAULT' },
          { currentClipData: splitClip }
        );
      } else {
        updateClipDataWithSplits({
          clipId: clipData.id,
          actionType: CLIP_UPDATE_TYPE.SPLIT_SEGMENT,
          startTime
        });
      }
      seekTo(startTime);
    },
    [clipData, hasExistingSplitAtPlayerTime, seekTo]
  );

  return {
    canUseSplits,
    reasonForDisabledSplits,
    performSplit
  };
}
